<template>
  <div>
    <DxPopup
      :visible="visible"
      :hide-on-outside-click="false"
      :show-close-button="false"
      :drag-enabled="false"
      :resize-enabled="false"
      :shading="false"
      :width="800"
      :height="560"
      @hidden="$emit('hidden')"
    >
      <template #title>
        <div class="lot-qty-title-block">
          <span class="lot-qty-title-main">{{ $t("lot_items.edit") }}</span>
          <span class="lot-qty-title-sub">
            {{ $t("lot_items.summary", { count: rows.length, total: selectedTotal, unit: measureUnitSymbol || "" }) }}
            &nbsp;/&nbsp; {{ $t("common.quantity") }}: {{ targetQuantity }} {{ measureUnitSymbol || "" }}
          </span>
        </div>
      </template>
      <template #content>
        <div class="lot-qty-wrap">
          <div class="lot-qty-toolbar">
            <SmagriaButton :label="$t('lot_items.add')" customClass="secondary border" :width="'140px'" :action="openAddLot" />
          </div>

          <DxDataGrid
            ref="grid"
            class="smagria-grid lot-qty-grid"
            :data-source="rows"
            key-expr="group_key"
            :show-borders="true"
            :column-auto-width="true"
            :remote-operations="false"
            :element-attr="gridAttr"
            height="340"
            @row-updated="onRowUpdated"
            @row-removed="onRowRemoved"
            @cell-prepared="onCellPrepared"
          >
            <DxEditing mode="cell" :allow-updating="true" :allowDeleting="true" :use-icons="true" :confirm-delete="false" />
            <DxScrolling mode="virtual" />
            <DxSorting mode="none" />

            <DxColumn data-field="lot" :caption="$t('lot_items.lot')" :allow-editing="false" :min-width="140" />
            <DxColumn
              data-field="expiration_date_display"
              :caption="$t('common.expiration_date')"
              :allow-editing="false"
              :allow-filtering="false"
              :allow-sorting="false"
              :min-width="130"
            />
            <DxColumn
              data-field="available_quantity"
              :caption="$t('lot_items.available_qty')"
              data-type="number"
              :allow-editing="false"
              :allow-filtering="false"
              :allow-sorting="false"
              alignment="right"
              :min-width="110"
            />
            <DxColumn
              data-field="quantity"
              :caption="$t('common.quantity')"
              data-type="number"
              :allow-editing="true"
              :allow-filtering="false"
              :allow-sorting="false"
              alignment="right"
              :min-width="110"
            >
              <DxRequiredRule :message="$t('validation.field_is_required')" />
            </DxColumn>
            <DxColumn
              data-field="measure_unit.name"
              :caption="$t('common.measure_unit')"
              :allow-editing="false"
              :allow-filtering="false"
              :allow-sorting="false"
              :min-width="90"
            />
          </DxDataGrid>

          <div class="lot-qty-actions">
            <SmagriaButton :label="$t('common.cancel')" customClass="secondary border" :width="'130px'" :action="hide" />
            <SmagriaButton :label="$t('lot_items.save')" customClass="primary" :width="'130px'" :action="onConfirm" />
          </div>
        </div>
      </template>
    </DxPopup>

    <LotAvailableSelectorDialog
      :visible="showAddLotSelector"
      :product-uuid="productUuid"
      :warehouse-uuid="warehouseUuid"
      :movement-date="movementDate"
      :measure-unit-symbol="measureUnitSymbol"
      :product-name="productName"
      :transaction-uuid="transactionUuid"
      @hidden="showAddLotSelector = false"
      @continue="onAddLotContinue"
    />
  </div>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import { DxDataGrid, DxColumn, DxEditing, DxScrolling, DxSorting, DxRequiredRule } from "devextreme-vue/data-grid";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import LotAvailableSelectorDialog from "./LotAvailableSelectorDialog.vue";
import { errorMessage } from "@/helpers/common";
import { splitQuantityAcrossLotRows } from "~/helpers/stockHelper";

export default {
  name: "LotQuantityEditorDialog",
  components: {
    DxPopup,
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxScrolling,
    DxSorting,
    DxRequiredRule,
    SmagriaButton,
    LotAvailableSelectorDialog,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    targetQuantity: {
      type: Number,
      default: 0,
    },
    measureUnitSymbol: {
      type: String,
      default: "",
    },
    productUuid: {
      type: String,
      default: null,
    },
    warehouseUuid: {
      type: String,
      default: null,
    },
    movementDate: {
      type: String,
      default: null,
    },
    productName: {
      type: String,
      default: "",
    },
    transactionUuid: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      rows: [],
      showAddLotSelector: false,
      gridAttr: { class: "smagria-grid lot-qty-grid" },
    };
  },
  computed: {
    selectedTotal() {
      return parseFloat(this.rows.reduce((sum, r) => sum + (parseFloat(r.quantity) || 0), 0).toFixed(4));
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.rows = this.items.map((item) => ({
          ...item,
          quantity: 0,
        }));
        // Auto-open lot selector when there are no rows to edit
        if (this.rows.length === 0) {
          this.$nextTick(() => {
            this.showAddLotSelector = true;
          });
        }
      }
    },
  },
  methods: {
    onRowUpdated(e) {
      const idx = this.rows.findIndex((r) => r.group_key === e.key);
      if (idx === -1) return;
      const row = this.rows[idx];
      let qty = parseFloat(row.quantity) || 0;
      const max = parseFloat(row.available_quantity) || 0;
      if (qty < 0) qty = 0;
      if (qty > max) qty = max;
      this.$set(this.rows, idx, { ...row, quantity: qty });
    },
    onCellPrepared(e) {
      if (e.rowType === "data" && e.column.dataField === "quantity" && e.column.allowEditing) {
        e.cellElement.classList.add("editable-cell-yellow");
      }
    },
    onRowRemoved(e) {
      this.rows = this.rows.filter((r) => r.group_key !== e.key);
    },
    openAddLot() {
      this.showAddLotSelector = true;
    },
    onAddLotContinue(selectedItems) {
      this.showAddLotSelector = false;
      const existingKeys = new Set(this.rows.map((r) => r.group_key));
      const newRows = selectedItems.filter((item) => !existingKeys.has(item.group_key)).map((item) => ({ ...item, quantity: 0 }));
      this.rows = [...this.rows, ...newRows];
    },
    hide() {
      this.$emit("hidden");
    },
    onConfirm() {
      // Validate each row: quantity must be > 0 and <= available_quantity
      const hasInvalid = this.rows.some((r) => {
        const qty = parseFloat(r.quantity) || 0;
        const max = parseFloat(r.available_quantity) || 0;
        return qty <= 0 || qty > max;
      });
      if (hasInvalid) {
        errorMessage(this, this.$t("lot_items.invalid_quantity"));
        return;
      }
      // Validate sum matches target
      const target = parseFloat(this.targetQuantity) || 0;
      if (Math.abs(this.selectedTotal - target) > 0.0001) {
        errorMessage(this, this.$t("lot_items.sum_mismatch"));
        return;
      }
      // Split each lot group quantity across its underlying stock movement
      // item rows (FIFO), so the payload stays row-level as before.
      const lotItems = this.rows.flatMap((r) => splitQuantityAcrossLotRows(r, parseFloat(r.quantity) || 0));
      this.$emit("confirmed", lotItems);
    },
  },
};
</script>

<style scoped>
.lot-qty-title-block {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.lot-qty-title-main {
  font-size: 17px;
  font-weight: 700;
  color: #323232;
}
.lot-qty-title-sub {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}
.lot-qty-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 6px 8px 8px;
  gap: 10px;
}
.lot-qty-toolbar {
  display: flex;
  justify-content: flex-start;
}
.lot-qty-delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
}
.lot-qty-delete-btn:hover {
  background: #ffeaea;
}
.lot-qty-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: auto;
  padding-top: 4px;
}
</style>
